/* ═══════════════════════════════════════════════════════════════
   TaskSheet — the ONE side panel for create AND view/edit (Decision 4).
   Built on kit ui/sheet, structured like ProjectFormPanel.tsx (dirty
   tracking, discard-confirm on close, focus captured/returned around the
   trigger). Field/links/attachments/subtasks/comments markup lives in
   ./sections/*; every permission check runs through ./taskPermissions.ts.

   Stays mounted ALWAYS (never `{open && <TaskSheet/>}`) so Radix's own
   close transition and focus-return-to-trigger actually run (DESIGN.md
   §7) — the caller (TasksKit.tsx) toggles `open` instead of
   mounting/unmounting.
   ═══════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Task,
  TaskComment,
  TaskLink,
  TaskAttachment,
  TaskPriority,
  TaskStatus,
} from '../../../shared/types';
import { Button } from '../../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../components/ui/sheet';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { TaskFields } from './sections/TaskFields';
import { TaskLinks } from './sections/TaskLinks';
import { TaskAttachments } from './sections/TaskAttachments';
import { TaskSubtasks } from './sections/TaskSubtasks';
import { TaskComments } from './sections/TaskComments';
import {
  canEditTask,
  canDeleteTask,
  canComment,
  canPickAssignees,
  assignableMembers,
} from './taskPermissions';
import type { TasksData } from './useTasksData';

export interface TaskSheetProps {
  data: TasksData;
  open: boolean;
  mode: 'create' | 'view';
  /** Required for `mode: 'view'`; ignored (may be null) for 'create'. */
  task: Task | null;
  createInStatus: TaskStatus;
  createPresetProjectId: string | null;
  perms: string[] | undefined;
  onClose: () => void;
  /** Clears the deep-link project preset — called whenever the CREATE
   *  panel closes, any path, so a later manual "+ New" never inherits a
   *  stale project (mirrors the old modal's unmount cleanup, P41). */
  clearPreset: () => void;
}

interface Draft {
  title: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  dueDate: string;
  projectIds: string[];
  assigneeIds: string[];
}

export function TaskSheet({
  data,
  open,
  mode,
  task,
  createInStatus,
  createPresetProjectId,
  perms,
  onClose,
  clearPreset,
}: TaskSheetProps) {
  const me = data.userId;
  const isCreate = mode === 'create';
  const pickAssignees = canPickAssignees(perms);

  const editable = isCreate
    ? true
    : task
      ? canEditTask(perms, me, data.members, task, data.assignments, data.tasks)
      : false;
  const allowDelete = !isCreate && canDeleteTask(perms);
  const allowComment = canComment(perms);

  function buildDraft(): Draft {
    if (!isCreate && task) {
      // Decision 20: task_project_links is the only project truth — the
      // deprecated task.projectId column is never read here.
      const projectIds = new Set<string>();
      data.taskProjectLinks
        .filter((lnk) => lnk.taskId === task.id)
        .forEach((lnk) => projectIds.add(lnk.projectId));
      return {
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        status: task.status,
        dueDate: task.dueDate || '',
        projectIds: Array.from(projectIds),
        assigneeIds: data.assignments
          .filter((a) => a.taskId === task.id)
          .map((a) => a.teamMemberId),
      };
    }
    // Create defaults — unchanged from today's Create dialog (P38), plus
    // Decision 14: without an assign permission, the new task is mine.
    return {
      title: '',
      description: '',
      priority: 'medium',
      status: createInStatus,
      dueDate: '',
      projectIds: createPresetProjectId ? [createPresetProjectId] : [],
      assigneeIds: pickAssignees ? [] : [me],
    };
  }

  const [draft, setDraft] = useState<Draft>(buildDraft);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [comments, setComments] = useState<TaskComment[]>([]);
  const [links, setLinks] = useState<TaskLink[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);

  // Reset the draft every time the panel opens — never while open and the
  // user is mid-edit (same rule as ProjectFormPanel).
  useEffect(() => {
    if (!open) return;
    setDraft(buildDraft());
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, task?.id]);

  // Load child collections for an existing task only — create mode has no
  // saved task yet, so links/files/subtasks/comments stay absent exactly
  // like today's Create dialog (do not invent them here).
  useEffect(() => {
    if (isCreate || !task) {
      setComments([]);
      setLinks([]);
      setAttachments([]);
      return;
    }
    // Spread into a fresh array — these list*() calls read straight from
    // cloudCache's query(), which returns the same reference it mutates in
    // place on every save (same trap as taskDataLoader.ts).
    data
      .listComments(task.id)
      .then((v) => setComments([...v]))
      .catch(() => {});
    data
      .listLinks(task.id)
      .then((v) => setLinks([...v]))
      .catch(() => {});
    data
      .listAttachments(task.id)
      .then((v) => setAttachments([...v]))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, task?.id]);

  const subtasks = !isCreate && task ? data.tasks.filter((t) => t.parentTaskId === task.id) : [];

  // Assignee picker options: who the viewer may newly assign, PLUS whoever
  // is already on the draft (Decision 21 — a deactivated current assignee
  // stays visible even though they drop out of the pickable list).
  const assigneeOptions = useMemo(() => {
    const pickable = assignableMembers(
      perms,
      me,
      data.members.filter((m) => m.active)
    );
    const byId = new Map<string, string>(pickable.map((m) => [m.id, m.name]));
    draft.assigneeIds.forEach((id) => {
      if (!byId.has(id)) byId.set(id, data.getMemberName(id));
    });
    return Array.from(byId.entries()).map(([value, label]) => ({ value, label }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms, me, data.members, draft.assigneeIds]);

  // Radix only auto-restores focus to a <SheetTrigger> ref; this kit's
  // sheet is opened imperatively from a row/card/button, so capture
  // whatever was focused right before open and restore it ourselves
  // (DESIGN.md §7 — focus returns to the trigger on close).
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  function update(patch: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }

  function doClose() {
    if (isCreate) clearPreset();
    onClose();
  }

  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else doClose();
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (isCreate) {
        const ok = await data.createTask({
          title: draft.title,
          description: draft.description,
          priority: draft.priority,
          status: draft.status,
          projectIds: draft.projectIds,
          dueDate: draft.dueDate,
          assigneeIds: draft.assigneeIds,
        });
        if (ok) {
          setDirty(false);
          doClose();
        }
      } else if (task) {
        const ok = await data.saveTaskDetail(task, {
          title: draft.title,
          description: draft.description,
          priority: draft.priority,
          status: draft.status,
          dueDate: draft.dueDate,
          projectIds: draft.projectIds,
          assigneeIds: draft.assigneeIds,
        });
        if (ok) setDirty(false);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!task) return;
    await data.deleteTask(task.id);
    setConfirmDelete(false);
    onClose();
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && requestClose()}>
        <SheetContent
          side="right"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>{isCreate ? 'New task' : draft.title || 'Task'}</SheetTitle>
            {!isCreate && task && (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
                Created by {data.getMemberName(task.createdBy)} —{' '}
                {new Date(task.createdAt).toLocaleDateString()}
              </div>
            )}
          </SheetHeader>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-4)',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <TaskFields
              editTitle={draft.title}
              onTitleChange={(v) => update({ title: v })}
              editStatus={draft.status}
              onStatusChange={(v) => update({ status: v })}
              editPriority={draft.priority}
              onPriorityChange={(v) => update({ priority: v })}
              editDueDate={draft.dueDate}
              onDueDateChange={(v) => update({ dueDate: v })}
              editProjectIds={draft.projectIds}
              onProjectIdsChange={(ids) => update({ projectIds: ids })}
              editDesc={draft.description}
              onDescChange={(v) => update({ description: v })}
              taskAssigneeIds={draft.assigneeIds}
              onAssigneeIdsChange={(ids) => update({ assigneeIds: ids })}
              assigneeOptions={assigneeOptions}
              canPickAssignees={pickAssignees}
              projects={data.projects}
              readOnly={!editable}
            />

            {!isCreate && task && (
              <>
                <TaskLinks
                  links={links}
                  canEdit={editable}
                  onAdd={async (url, label) => {
                    const link = await data.addLink(task.id, url, label);
                    if (link) setLinks((prev) => [...prev, link]);
                  }}
                  onDelete={async (id) => {
                    await data.deleteLink(id);
                    setLinks((prev) => prev.filter((l) => l.id !== id));
                  }}
                />

                <TaskAttachments
                  attachments={attachments}
                  canEdit={editable}
                  onAdd={async () => {
                    const att = await data.addAttachment(task.id);
                    if (att) setAttachments((prev) => [...prev, att]);
                  }}
                  onDelete={async (id) => {
                    await data.deleteAttachment(id);
                    setAttachments((prev) => prev.filter((a) => a.id !== id));
                  }}
                />

                <TaskSubtasks
                  subtasks={subtasks}
                  canEdit={editable}
                  onAdd={(title) => data.addSubtask(task, title, draft.projectIds[0])}
                  onToggle={(sub) => data.toggleSubtaskDone(sub)}
                />

                <TaskComments
                  comments={comments}
                  canComment={allowComment}
                  getMemberName={data.getMemberName}
                  onAdd={async (text) => {
                    await data.addComment(task, text);
                    const updated = await data.listComments(task.id);
                    setComments([...updated]);
                  }}
                />
              </>
            )}
          </div>

          <SheetFooter style={{ justifyContent: allowDelete ? 'space-between' : 'flex-end' }}>
            {allowDelete && (
              <Button
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                Delete
              </Button>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button variant="outline" onClick={requestClose} disabled={saving}>
                {isCreate ? 'Cancel' : 'Close'}
              </Button>
              {editable && (isCreate || dirty) && (
                <Button onClick={() => void handleSave()} disabled={saving}>
                  {saving ? 'Saving…' : isCreate ? 'Create task' : 'Save changes'}
                </Button>
              )}
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard changes?"
        description="You have unsaved edits to this task. Discarding closes the panel without saving."
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          setDirty(false);
          doClose();
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this task?"
        description="Delete this task and all subtasks?"
        confirmLabel="Delete"
        destructive
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}
